import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import callbacksService from '../../services/callbacks.service';
import workflowService, { type PendingItem } from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { isInReview } from '../../lib/onboardingStatus';
import { colors, gradients } from '../../styles/theme';

/** Mirror of `MACRO_PHASES` in Onboarding.tsx. Re-declared here (not
 *  imported) so this card stays decoupled from the wizard module — if
 *  the wizard ever splits phases differently, the engagement view can
 *  evolve at its own pace. Order matches workflow display_order. */
const PHASES = [
  { slug: 'pending_profile',   label: 'Personal data' },
  { slug: 'pending_pep_aml',   label: 'PEP / AML' },
  { slug: 'pending_documents', label: 'Documents' },
  { slug: 'under_review',      label: 'Under review' },
  { slug: 'approved',          label: 'Approved' },
] as const;

type PhaseState = 'done' | 'current' | 'pending';

function phaseState(i: number, activeIdx: number): PhaseState {
  if (i < activeIdx) return 'done';
  if (i === activeIdx) return 'current';
  return 'pending';
}

function stylesForState(state: PhaseState): {
  dotBg: string;
  dotFg: string;
  labelColor: string;
} {
  if (state === 'done') {
    return { dotBg: colors.success, dotFg: '#FFFFFF', labelColor: colors.ink };
  }
  if (state === 'current') {
    return { dotBg: colors.gold, dotFg: '#FFFFFF', labelColor: colors.ink };
  }
  return { dotBg: colors.border, dotFg: colors.muted, labelColor: colors.muted };
}

/** Map any workflow state slug to a phase index. Identical mapping to
 *  `macroPhaseIndex` in Onboarding.tsx — keep them in sync if the
 *  workflow definitions change. */
function phaseIndex(slug: string | null): number {
  if (slug === null) return -1;                        // no case yet
  const direct = PHASES.findIndex((p) => p.slug === slug);
  if (direct !== -1) return direct;
  if (slug === 'registered') return -1;                 // not started
  if (slug === 'information_required') return 2;        // re-work Documents
  if (['rejected', 'archived', 'restricted', 'pending_rejection', 'pending_rejection_review'].includes(slug)) return 3;
  return 3;
}

interface Props {
  /** Current onboarding state slug (or null for "not started yet"). */
  currentSlug: string | null;
  /** Active onboarding case id — enables the self-service "items left"
   *  list. Omitted/null when there is no case yet (nothing to fetch). */
  caseId?: string | null;
}

export default function OnboardingProgressCard({
  currentSlug,
  caseId = null,
}: Readonly<Props>): JSX.Element {
  const activeIdx = phaseIndex(currentSlug);
  // Step counter is 1-indexed and clamped to the visible range so the
  // "step X of Y" copy never reads "step 0" (which a brand-new
  // `registered` case would otherwise produce).
  const stepNumber = Math.max(1, activeIdx + 1);
  // True for every state where the case is waiting on staff (under_review
  // plus the internal pending_decision / pending_rejection sub-states). In
  // those states the client has nothing to "continue", so the card switches
  // to a status read-out instead of an action CTA.
  const inReview = isInReview(currentSlug);

  // Self-service: surface exactly what the client still owes for the
  // current capture state so they can finish without waiting for an agent
  // to call. Skipped in review/terminal states (nothing to complete) and
  // when there is no case yet. A failed load is said out loud rather than shown
  // as an empty list, which would tell the client nothing is left to do.
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [pendingError, setPendingError] = useState(false);
  useEffect(() => {
    setPendingError(false);
    if (caseId === null || inReview) {
      setPending([]);
      return;
    }
    let cancelled = false;
    workflowService
      .getPendingItems(caseId)
      .then((res) => {
        if (!cancelled) setPending(res.can_complete ? res.items : []);
      })
      .catch((err: unknown) => {
        console.warn('pending items unavailable', err);
        if (!cancelled) {
          setPending([]);
          setPendingError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, inReview, currentSlug]);

  const ctaLabel = inReview ? 'View status' : 'Continue onboarding';
  const headline = inReview
    ? "We're reviewing your application"
    : "Your onboarding progress";
  const subtext = (() => {
    if (inReview) {
      return 'Our compliance team is checking your information. We will email you with the outcome.';
    }
    if (pendingError) {
      return 'We could not check what is still needed. Open your application to see exactly where you are.';
    }
    if (pending.length > 0) {
      const noun = pending.length === 1 ? 'item' : 'items';
      return `You have ${pending.length} ${noun} left to complete before we can review your account.`;
    }
    return `You're on step ${stepNumber} of ${PHASES.length}. A few minutes more and you're done.`;
  })();

  return (
    <section
      className="rounded-2xl p-6"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
      aria-labelledby="onboarding-progress-heading"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div className="min-w-0">
          <h2
            id="onboarding-progress-heading"
            className="text-lg font-semibold mb-1"
            style={{ color: colors.ink }}
          >
            {headline}
          </h2>
          <p className="text-sm" style={{ color: colors.muted }}>
            {subtext}
          </p>
        </div>
        <Link
          to="/client/onboarding"
          className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          {ctaLabel} →
        </Link>
      </div>

      {/* Phase dots + connectors */}
      <div className="flex items-center" style={{ gap: 0 }}>
        {PHASES.map((phase, i) => {
          const state = phaseState(i, activeIdx);
          const isLast = i === PHASES.length - 1;
          const { dotBg, dotFg, labelColor } = stylesForState(state);

          return (
            <div key={phase.slug} className="flex-1 flex items-center min-w-0">
              <div className="flex flex-col items-center" style={{ minWidth: 0 }}>
                <div
                  className="rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    width: 28, height: 28,
                    background: dotBg, color: dotFg,
                    border: state === 'current' ? `2px solid ${colors.gold}` : 'none',
                  }}
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  {state === 'done' ? '✓' : i + 1}
                </div>
                <span
                  className="text-xs mt-2 text-center"
                  style={{ color: labelColor, maxWidth: 90 }}
                >
                  {phase.label}
                </span>
              </div>
              {!isLast && (
                <div
                  className="flex-1 h-px mx-2"
                  style={{
                    background: i < activeIdx ? colors.success : colors.border,
                    marginTop: -22, // align with dot centres
                  }}
                  aria-hidden
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Self-service: the specific required items still outstanding for the
          current capture state. Lets the client act without an agent nudge —
          the "Continue onboarding" CTA above deep-links into the wizard. */}
      {!inReview && pending.length > 0 && (
        <div
          className="mt-6 rounded-xl px-4 py-3"
          style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
        >
          <p className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>
            Still needed to finish
          </p>
          <ul className="text-sm space-y-1" style={{ color: colors.inkSoft }}>
            {pending.slice(0, 6).map((it) => (
              <li key={it.field_key} className="flex items-center gap-2">
                <span aria-hidden style={{ color: colors.gold }}>•</span>
                {it.label}
              </li>
            ))}
            {pending.length > 6 && (
              <li style={{ color: colors.muted }}>+{pending.length - 6} more</li>
            )}
          </ul>
        </div>
      )}

      {/* GitLab #49 follow-up: under-review only — the "what happens next"
          accordion + "Message your advisor" CTA so the silent wait period
          doesn't read as a dead end. Hidden in every other state because
          the phase stepper above is already actionable in those. */}
      {inReview && <UnderReviewNextSteps />}
    </section>
  );
}

// ─── #49: under-review next-steps panel ────────────────────────────

type CallbackUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'requested'; advisorName: string | null; alreadyExisted: boolean; assigned: boolean }
  | { kind: 'error'; message: string };

function UnderReviewNextSteps(): JSX.Element {
  const [state, setState] = useState<CallbackUiState>({ kind: 'idle' });

  async function handleMessage(): Promise<void> {
    if (state.kind === 'submitting' || state.kind === 'requested') return;
    setState({ kind: 'submitting' });
    try {
      const res = await callbacksService.requestCallback({
        context_note: 'From under-review onboarding card.',
      });
      const advisorName =
        res.advisor_first_name || res.advisor_last_name
          ? `${res.advisor_first_name ?? ''} ${res.advisor_last_name ?? ''}`.trim()
          : null;
      setState({
        kind: 'requested',
        advisorName,
        alreadyExisted: res.already_existed,
        assigned: res.assigned,
      });
    } catch (err: unknown) {
      setState({
        kind: 'error',
        message: getApiErrorMessage(
          err,
          "Couldn't reach your Account Executive right now. Please try again.",
        ),
      });
    }
  }

  const isRequested = state.kind === 'requested';
  const isSubmitting = state.kind === 'submitting';

  return (
    <div className="mt-6 space-y-4">
      <details
        className="rounded-xl overflow-hidden"
        style={{ border: `1px solid ${colors.border}` }}
      >
        <summary
          className="px-4 py-3 text-sm font-semibold cursor-pointer select-none"
          style={{ color: colors.ink, background: colors.canvas, listStyle: 'none' }}
        >
          What happens next?
        </summary>
        <div
          className="px-4 py-3 text-sm space-y-2"
          style={{ color: colors.inkSoft }}
        >
          <p>
            Once approved, you&apos;ll get a confirmation email and your account summary becomes active.
          </p>
          <p>
            If our team needs anything else from you, you&apos;ll receive an email
            with the specific items to address and this page will reopen so you
            can resubmit directly.
          </p>
          <p>
            For cases that require additional compliance steps, your assigned Account Executive may contact you by phone.
          </p>
        </div>
      </details>

      <div
        className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 flex-wrap"
        style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      >
        <p className="text-sm" style={{ color: colors.inkSoft }}>
          Have questions while you wait?
        </p>
        <button
          type="button"
          onClick={handleMessage}
          disabled={isSubmitting || isRequested}
          className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          style={{
            background: isRequested ? colors.successBg : colors.surface,
            color: isRequested ? colors.success : colors.gold,
            border: `1px solid ${isRequested ? colors.successBorder : colors.gold}`,
            cursor: isSubmitting || isRequested ? 'default' : 'pointer',
          }}
        >
          {isSubmitting && 'Requesting…'}
          {state.kind === 'idle' && 'Message your Account Executive →'}
          {isRequested && (
            <>
              ✓{' '}
              {state.assigned ? (
                <>
                  {state.alreadyExisted
                    ? 'Your Account Executive already has this request'
                    : 'Your Account Executive has been notified'}
                  {state.advisorName && !state.alreadyExisted
                    ? ` — ${state.advisorName} will reach out`
                    : ''}
                </>
              ) : (
                "we're connecting you with an Account Executive who'll reach out"
              )}
            </>
          )}
          {state.kind === 'error' && 'Try again'}
        </button>
      </div>
      {state.kind === 'error' && (
        <p className="text-xs" style={{ color: colors.danger }}>
          {state.message}
        </p>
      )}
    </div>
  );
}
